/**
 * useWeather — TanStack Query hook over GET /api/weather/current.
 *
 * Plan 01-14 Task 2.  Backend (Plan 07 weather.service.ts) returns the
 * already-transformed WeatherView shape (D-12) — never raw Met.no body.
 * The hook therefore consumes the response directly with no client-side
 * transform.
 *
 * Cadence:
 *   - staleTime 60_000ms   — dashboard only re-renders the temp once a minute.
 *   - refetchInterval 60s  — pull a fresh row once a minute (the backend's
 *     own 15-min Met.no floor + circuit breaker means most of these are
 *     served from the in-memory cache — cheap and consistent).
 *   - retry 2              — flaky home network deserves a couple retries
 *     before showing the unavailable state.
 *
 * Error contract:  the route serves the last cached body with `stale: true`
 * during a Met.no outage, so a true `error` here means the BACKEND is
 * unreachable (Pi rebooting, etc.) — `WeatherTile` then renders
 * `t('weather.unavailable')` instead of a partial UI.
 */
import { useQuery } from '@tanstack/react-query';

export type WeatherCurrent = {
  temperature: number;
  feels_like: number;
  wind_ms: number;
  precipitation_mm: number;
  symbol_code: string;
};

export type WeatherHourly = {
  time: string;
  temperature: number;
  symbol_code: string;
};

export type WeatherResponse = {
  fetched_at: string;
  stale: boolean;
  current: WeatherCurrent;
  hourly: WeatherHourly[];
  hi_lo: { hi: number; lo: number };
  location: { lat: number; lon: number };
};

export function useWeather() {
  return useQuery<WeatherResponse>({
    queryKey: ['weather', 'current'],
    queryFn: async () => {
      const r = await fetch('/api/weather/current', {
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`weather_fetch_failed: ${r.status}`);
      return (await r.json()) as WeatherResponse;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 2,
  });
}
